/** One Auth0 connection editor — the AUTH_METHODS_CONFIG control's building
 *  block (formerly exported by the create wizard's Step6Authentication; the
 *  routed setup wizard dropped that step, the editor lives on here). */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import type { AuthMethodInput } from '../wizard/types';

export const EMPTY_AUTH_METHOD: AuthMethodInput = {
  name: '',
  domain: '',
  client_id: '',
  allow_signup: false,
  allowed_domains: [],
  access_group_codes: [],
};

interface AuthSectionProps {
  label: string;
  methods: AuthMethodInput[];
  onUpdate: (methods: AuthMethodInput[]) => void;
  /** Available group codes from step 5 for the access_group_codes picker. */
  groupCodes: string[];
}

/** One Auth0 connection editor (reused by the create wizard + the preflight
 *  AUTH_METHODS_CONFIG control). Enable toggle → single method; access groups are
 *  referenced by codename (portable across create + preflight). */
export function AuthSection({ label, methods, onUpdate, groupCodes }: AuthSectionProps) {
  const [enabled, setEnabled] = useState(methods.length > 0);
  const [domainInput, setDomainInput] = useState('');
  const [groupCodeInput, setGroupCodeInput] = useState('');
  const method = methods[0] ?? { ...EMPTY_AUTH_METHOD };

  function toggle(checked: boolean) {
    setEnabled(checked);
    onUpdate(checked ? [{ ...EMPTY_AUTH_METHOD }] : []);
  }

  function patch(partial: Partial<AuthMethodInput>) {
    onUpdate([{ ...method, ...partial }]);
  }

  function addDomain() {
    const v = domainInput.trim().toLowerCase();
    if (v && !method.allowed_domains.includes(v)) {
      patch({ allowed_domains: [...method.allowed_domains, v] });
    }
    setDomainInput('');
  }

  function removeDomain(d: string) {
    patch({ allowed_domains: method.allowed_domains.filter((x) => x !== d) });
  }

  function addGroupCode() {
    const v = groupCodeInput.trim();
    if (v && !method.access_group_codes.includes(v)) {
      patch({ access_group_codes: [...method.access_group_codes, v] });
    }
    setGroupCodeInput('');
  }

  function removeGroupCode(c: string) {
    patch({ access_group_codes: method.access_group_codes.filter((x) => x !== c) });
  }

  return (
    <Accordion defaultExpanded={false} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabled}
                onChange={(e) => { e.stopPropagation(); toggle(e.target.checked); }}
                onClick={(e) => e.stopPropagation()}
              />
            }
            label={label}
            sx={{ m: 0 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {!enabled ? (
          <Typography variant="caption" color="text.secondary">Enable Auth0 above to configure.</Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Display name"
                fullWidth size="small"
                value={method.name}
                onChange={(e) => patch({ name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Auth0 domain"
                fullWidth size="small"
                placeholder="example.auth0.com"
                value={method.domain}
                onChange={(e) => patch({ domain: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Client ID"
                fullWidth size="small"
                value={method.client_id}
                onChange={(e) => patch({ client_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={method.allow_signup}
                    onChange={(e) => patch({ allow_signup: e.target.checked })}
                  />
                }
                label="Allow new user sign-up via this provider"
              />
            </Grid>
            {method.allow_signup && (
              <>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Allowed email domains (staff users only — leave empty to block all auto-signup).
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
                    <TextField
                      label="Domain (e.g. example.com)"
                      size="small"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {method.allowed_domains.map((d) => (
                      <Chip key={d} label={d} size="small" onDelete={() => removeDomain(d)} />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Access groups to assign to new users (by code from step 5 + defaults).
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
                    <TextField
                      label="Group code"
                      size="small"
                      value={groupCodeInput}
                      onChange={(e) => setGroupCodeInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroupCode(); } }}
                      sx={{ flex: 1 }}
                      helperText={groupCodes.length ? `Codes from step 5: ${groupCodes.join(', ')}` : undefined}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {method.access_group_codes.map((c) => (
                      <Chip key={c} label={c} size="small" onDelete={() => removeGroupCode(c)} />
                    ))}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
